/**
 * Word-level diff utility for the edit operation
 * 
 * Computes the difference between two strings at the word level,
 * producing a sequence of insert/delete operations that can be
 * applied with track changes.
 */

// ===========================================
// TYPES
// ===========================================

export type DiffOp = 
  | { type: "equal"; text: string }
  | { type: "insert"; text: string }
  | { type: "delete"; text: string };

export interface WordDiffResult {
  ops: DiffOp[];
  hasChanges: boolean;
}

// ===========================================
// TOKENIZATION
// ===========================================

/**
 * Tokenize text into words while preserving whitespace
 * Each token is either a word or whitespace
 */
function tokenize(text: string): string[] {
  // Split on word boundaries while keeping the delimiters
  // This regex matches: word characters OR whitespace/punctuation
  const tokens: string[] = [];
  let current = "";
  let inWord = false;

  for (const char of text) {
    const isWordChar = /\w/.test(char);
    
    if (isWordChar !== inWord) {
      if (current) {
        tokens.push(current);
      }
      current = char;
      inWord = isWordChar;
    } else {
      current += char;
    }
  }
  
  if (current) {
    tokens.push(current);
  }

  return tokens;
}

// ===========================================
// DIFF ALGORITHM (Myers-like LCS approach)
// ===========================================

/**
 * Compute the longest common subsequence of two arrays
 */
function computeLCS<T>(a: T[], b: T[]): T[] {
  const m = a.length;
  const n = b.length;
  
  // Build LCS table
  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  
  // Backtrack to find LCS
  const lcs: T[] = [];
  let i = m, j = n;
  
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      lcs.unshift(a[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }
  
  return lcs;
}

/**
 * Compute word-level diff between two strings
 */
export function computeWordDiff(oldText: string, newText: string): WordDiffResult {
  // Handle edge cases
  if (oldText === newText) {
    return {
      ops: oldText ? [{ type: "equal", text: oldText }] : [],
      hasChanges: false,
    };
  }

  if (!oldText) {
    return {
      ops: [{ type: "insert", text: newText }],
      hasChanges: true,
    };
  }

  if (!newText) {
    return {
      ops: [{ type: "delete", text: oldText }],
      hasChanges: true,
    };
  }

  // Tokenize both texts
  const oldTokens = tokenize(oldText);
  const newTokens = tokenize(newText);

  // Compute LCS
  const lcs = computeLCS(oldTokens, newTokens);

  // Generate diff operations
  const ops: DiffOp[] = [];
  let oldIdx = 0;
  let newIdx = 0;
  let lcsIdx = 0;

  while (oldIdx < oldTokens.length || newIdx < newTokens.length) {
    // Check if current tokens match the LCS
    const oldToken = oldTokens[oldIdx];
    const newToken = newTokens[newIdx];
    const lcsToken = lcs[lcsIdx];

    if (oldToken === lcsToken && newToken === lcsToken) {
      // Both match LCS - equal
      ops.push({ type: "equal", text: oldToken });
      oldIdx++;
      newIdx++;
      lcsIdx++;
    } else if (oldToken !== lcsToken && newToken === lcsToken) {
      // Old doesn't match, new does - delete old
      ops.push({ type: "delete", text: oldToken });
      oldIdx++;
    } else if (oldToken === lcsToken && newToken !== lcsToken) {
      // Old matches, new doesn't - insert new
      ops.push({ type: "insert", text: newToken });
      newIdx++;
    } else {
      // Neither matches - delete old, insert new
      if (oldIdx < oldTokens.length) {
        ops.push({ type: "delete", text: oldToken });
        oldIdx++;
      }
      if (newIdx < newTokens.length && oldIdx >= oldTokens.length) {
        ops.push({ type: "insert", text: newToken });
        newIdx++;
      }
    }
  }

  // Merge consecutive operations of the same type
  const mergedOps = mergeConsecutiveOps(ops);

  return {
    ops: mergedOps,
    hasChanges: mergedOps.some(op => op.type !== "equal"),
  };
}

/**
 * Merge consecutive operations of the same type
 */
function mergeConsecutiveOps(ops: DiffOp[]): DiffOp[] {
  if (ops.length === 0) return [];

  const merged: DiffOp[] = [];
  let current = { ...ops[0] };

  for (let i = 1; i < ops.length; i++) {
    const op = ops[i];
    if (op.type === current.type) {
      current.text += op.text;
    } else {
      merged.push(current);
      current = { ...op };
    }
  }

  merged.push(current);
  return merged;
}

/**
 * Convert diff result to a human-readable format (for debugging)
 */
export function diffToString(result: WordDiffResult): string {
  return result.ops.map(op => {
    switch (op.type) {
      case "equal":
        return op.text;
      case "insert":
        return `[+${op.text}]`;
      case "delete":
        return `[-${op.text}]`;
    }
  }).join("");
}
